/**
 * @file 入力要素（Atom）。input要素のラッパー。registerとonChangeの同時指定を禁止するバリデーションを持つ。
 *        registerの有無で制御/非制御を切り替える。
 */
import { ChangeEventHandler, HTMLInputTypeAttribute } from 'react';
import { FieldError, UseFormRegisterReturn } from 'react-hook-form';

/** 属性定義 */
type Props = Partial<{
  type: HTMLInputTypeAttribute;
  name: string;
  id: string;
  className: string;
  defaultValue: string | number | readonly string[];
  value: string | number | readonly string[];
  autoComplete: string;
  placeholder: string;
  defaultChecked: boolean;
  checked: boolean;
  disabled: boolean;
  maxLength: number;
  minLength: number;
  required: boolean;
  onChange: ChangeEventHandler<HTMLInputElement>;
  register: UseFormRegisterReturn;
  error: FieldError;
  testid: string;
}>;

/**
 * コンポーネント描画
 * @param 属性定義
 * @returns コンポーネント
 */
const AtInput = ({
  type,
  id,
  name,
  className,
  autoComplete,
  defaultValue,
  value,
  placeholder,
  defaultChecked,
  checked,
  disabled,
  maxLength,
  minLength,
  required,
  onChange,
  register,
  error,
  testid,
}: Props) => {
  // react-hook-formのregisterは内部でonChangeを管理するため、外部onChangeとの併用は
  // イベントハンドラ競合を起こす。開発時に即座に気づけるようDOMExceptionで明示的にエラーを投げる
  if (register !== undefined && onChange !== undefined) {
    throw new DOMException('registerでonChangeを指定してください。');
  }
  return (
    <>
      {/* register未指定時: 非制御コンポーネントとして描画（onChange直接指定） */}
      {register === undefined && (
        <input
          type={type}
          id={id}
          name={name}
          className={`m-form-text ${className || ''}`}
          defaultValue={defaultValue}
          value={value}
          autoComplete={autoComplete}
          placeholder={placeholder}
          defaultChecked={defaultChecked}
          checked={checked}
          disabled={disabled}
          maxLength={maxLength}
          minLength={minLength}
          required={required}
          onChange={onChange}
          aria-invalid={!!error}
          data-testid={testid}
        />
      )}
      {/* register指定時: react-hook-form制御コンポーネントとして描画（nameやonChangeはregisterから展開） */}
      {register !== undefined && (
        <input
          type={type}
          id={id}
          className={`m-form-text ${className || ''}`}
          defaultValue={defaultValue}
          value={value}
          autoComplete={autoComplete}
          placeholder={placeholder}
          defaultChecked={defaultChecked}
          checked={checked}
          disabled={disabled}
          maxLength={maxLength}
          minLength={minLength}
          required={required}
          {...register}
          aria-invalid={!!error}
          data-testid={testid}
        />
      )}
    </>
  );
};

export default AtInput;
